import { useState } from 'react';
import { BrainCircuit } from 'lucide-react';
import type { ThreeWhysData } from '../../types/mavis';

interface WhyConfig {
  key: keyof ThreeWhysData;
  label: string;
  subtitle: string;
  placeholder: string;
  accentClass: string;
  dotClass: string;
}

const WHYS: WhyConfig[] = [
  {
    key: 'why_change',
    label: 'Why Change?',
    subtitle: 'The case for doing something different',
    placeholder:
      'Why must this customer change from the status quo? What is the cost of staying with their current approach? What pain, risk, or missed opportunity forces the question?',
    accentClass: 'border-l-4 border-indigo-200',
    dotClass: 'bg-indigo-500',
  },
  {
    key: 'why_now',
    label: 'Why Now?',
    subtitle: 'The urgency and timing argument',
    placeholder:
      'What makes this the right moment? Is there a compliance deadline, leadership change, competitive pressure, or strategic initiative creating urgency? What happens if they delay six months?',
    accentClass: 'border-l-4 border-amber-200',
    dotClass: 'bg-amber-500',
  },
  {
    key: 'why_us',
    label: 'Why Us?',
    subtitle: 'Our unique right to win',
    placeholder:
      "Why are we the right vendor for this deal? What differentiates our solution for this customer's specific situation, use case, or persona? What would we lose to if we lost?",
    accentClass: 'border-l-4 border-forest-200',
    dotClass: 'bg-forest-500',
  },
];

const textArea =
  'w-full bg-transparent border-0 focus:outline-none resize-none leading-relaxed placeholder:text-app-muted text-sm text-app-ink';

interface Props {
  data: ThreeWhysData;
  saving: boolean;
  onSave: (updated: ThreeWhysData) => void;
}

export default function MavisThreeWhysPanel({ data, saving: _saving, onSave }: Props) {
  const [draft, setDraft] = useState<ThreeWhysData>(data);

  const update = (key: keyof ThreeWhysData, value: string) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  };

  const handleBlur = () => {
    onSave(draft);
  };

  return (
    <div className="space-y-4">
      {WHYS.map(w => (
        <div
          key={w.key}
          className={`bg-app-surface rounded-2xl border border-app-line shadow-sm ${w.accentClass}`}
        >
          {/* Card header */}
          <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-3">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${w.dotClass}`} />
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-app-ink">{w.label}</h4>
              <p className="text-[11px] text-app-muted">{w.subtitle}</p>
            </div>
          </div>

          {/* Textarea */}
          <div className="px-5 py-4">
            <textarea
              value={draft[w.key]}
              onChange={e => update(w.key, e.target.value)}
              onBlur={handleBlur}
              rows={5}
              className={textArea}
              placeholder={w.placeholder}
            />

            {/* AI assist placeholder */}
            <div className="mt-3 pt-3 border-t border-app-line">
              <button
                disabled
                className="flex items-center gap-1.5 text-[11px] text-app-muted cursor-not-allowed"
                title="MAVIS AI assist — coming soon"
              >
                <BrainCircuit className="w-3.5 h-3.5" />
                Ask MAVIS
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
